import * as React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    CardContent,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    debounce,
    Button,
    Divider
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import { EnhancedTableHeadProps, HeadCell, ArrangementOrder } from 'types';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import UserEdit from './userEdit';
import UserAdd from './userAdd';
import { useCallback, useEffect, useState } from 'react';
import SkeletonBody from 'ui-component/skeleton/SkeletonBody';
import UserListMobile from './userListMobile';
import Utilities from 'utils/utilities';
import User from 'types/user';
import UserFilter from 'types/filters/userFilter';
import UserService from 'services/UserService';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
// table header options
const headCells: HeadCell[] = [
    {
        id: 'name',
        numeric: false,
        label: 'NAME',
        align: 'left'
    },
    {
        id: 'surname',
        numeric: false,
        label: 'SURNAME',
        align: 'left'
    },
    {
        id: 'username',
        numeric: false,
        label: 'Email',
        align: 'left'
    },
    {
        id: 'company',
        numeric: false,
        label: 'COMPANY',
        align: 'left'
    },
    {
        id: 'roles',
        numeric: false,
        label: 'ROLES',
        align: 'left'
    }
];

// ==============================|| TABLE HEADER ||============================== //

interface UserListEnhancedTableHeadProps extends EnhancedTableHeadProps {
    selected: string[];
}

function EnhancedTableHead({
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    selected
}: UserListEnhancedTableHeadProps) {
    const { t } = useTranslation();
    const currentTheme = useTheme();
    const mobileDevice = useMediaQuery(currentTheme.breakpoints.down('md'));
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
    }, []);

    return (
        <TableHead>
            <TableRow>
                {!mobileDevice &&
                    headCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.align}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                        >
                            {t(headCell.label)}
                        </TableCell>
                    ))}
                {numSelected <= 0 && (
                    <TableCell
                        sortDirection={false}
                        align="right"
                        sx={{ paddingRight: windowWidth < 1400 ? '1.5%!important' : '2%!important' }}
                    >
                        {t('ACTIONS')}
                    </TableCell>
                )}
            </TableRow>
        </TableHead>
    );
}

// ==============================|| USER LIST ||============================== //

const UserList = () => {
    const theme = useTheme();
    const { user: loggedUser } = useAuth();
    const { t } = useTranslation();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const utilities = new Utilities();

    const [order, setOrder] = useState<ArrangementOrder>('asc');
    const [orderBy, setOrderBy] = useState<string>('name');
    const [selected, setSelected] = useState<string[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);
    const [searchText, setSearchText] = useState<string>('');
    const [rows, setRows] = useState<User[]>([]);
    const [totalElements, setTotalElements] = useState<number>(0);
    const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
    const [userToAdd, setUserToAdd] = useState<User | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [fliterDialogModalOpen, setFliterDialogModalOpen] = useState(false);
    const userService = new UserService();

    useEffect(() => {
        (async () => {
            if (loggedUser) {
                setIsLoading(true);
                const userFilter = getUserFilter();
                await fetchRow(userFilter);
            }
        })();
    }, [loggedUser, page, rowsPerPage, order, orderBy, searchText]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
    }, []);

    const fetchRow = useCallback(
        debounce(async (userFilter: UserFilter) => {
            const paginatedUsers = await userService.getUsers(userFilter);
            setIsLoading(false);
            setRows(paginatedUsers.items);
            setTotalElements(paginatedUsers.totalCount);
        }, 700),
        []
    );

    const getUserFilter = () => {
        const userFilter: UserFilter = {
            orderAscending: order?.toString() === 'asc',
            orderBy: orderBy,
            page: page + 1,
            recordsPerPage: rowsPerPage,
            searchString: searchText
        };
        return userFilter;
    };

    const handleSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        const newString = event?.target.value;
        setSearchText(newString || '');
    };

    const handleRequestSort = (event: React.SyntheticEvent<Element, Event>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedId = rows.map((n) => n.name);
            setSelected(newSelectedId);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        event?.target.value && setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const onAdd = async (user: User) => {
        if (user && utilities.isNotNullOrEmpty(user.username!)) {
            if (user) {
                setIsLoading(true);
                const response = await userService.addUser(user);
                if (response) {
                    setUserToAdd(undefined);
                    const userFilter = getUserFilter();
                    await fetchRow(userFilter);
                    dispatch(startSnackbar(t('USER_ADDED_SUCCESSFULLY'), 'success'));
                } else {
                    setIsLoading(false);
                }
            }
        } else {
            dispatch(startSnackbar(t('EMAIL_IS_MANDATORY'), 'error'));
        }
        setIsLoading(false);
    };

    const onSave = async (user: User) => {
        if (user && utilities.isNotNullOrEmpty(user.username!)) {
            setIsLoading(true);
            user.tenantId = loggedUser!.tenantId;
            const response = await userService.editUser(user);
            if (response) {
                setUserToEdit(undefined);
                const userFilter = getUserFilter();
                await fetchRow(userFilter);
                dispatch(startSnackbar(t('USER_EDITED_SUCCESSFULLY'), 'success'));
            } else {
                setIsLoading(false);
            }
        } else {
            dispatch(startSnackbar(t('EMAIL_IS_MANDATORY'), 'error'));
        }
    };

    const onDelete = async (user: User) => {
        setIsLoading(true);
        setUserToEdit(undefined);
        const response = await userService.deleteUser(user.id, loggedUser?.tenantId!);
        if (response) {
            const userFilter = getUserFilter();
            await fetchRow(userFilter);
            dispatch(startSnackbar(t('USER_DELETED_SUCCESSFULLY'), 'success'));
        } else {
            setIsLoading(false);
            dispatch(startSnackbar(t('ERROR_DURING_DELETE_USER'), 'error'));
        }
    };

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    const handleChangeOrder = (e: any) => {
        const { value } = utilities.getFormValue(e);
        setOrderBy(value);
    };

    const handleChangeOrderMethod = (e: any) => {
        const { value } = utilities.getFormValue(e);
        setOrder(value === 'asc' ? 'asc' : 'desc');
    };

    const setDefaultFilters = () => {
        setOrder('asc');
        setOrderBy('name');
    };

    return (
        <MainCard title="Users" content={false}>
            <CardContent>
                <Grid container justifyContent="space-between" sx={{ mb: '1vh' }}>
                    <Drawer
                        anchor="right"
                        open={fliterDialogModalOpen}
                        onClose={() => setFliterDialogModalOpen(false)}
                        PaperProps={{
                            sx: { width: { xs: '75%', md: '50%', lg: '25%' } }
                        }}
                    >
                        <Grid container direction="column" spacing={2} sx={{ p: '15px' }}>
                            <Grid item justifyContent="center" textAlign="center">
                                <Grid container direction="row" alignItems="center">
                                    <Typography textAlign="center" variant="h5">
                                        {t('ADVANCED_FILTERS')}
                                    </Typography>
                                    <Tooltip title={t('CLOSE')}>
                                        <IconButton
                                            sx={{ marginLeft: 'auto', p: 0 }}
                                            onClick={() => {
                                                setFliterDialogModalOpen(false);
                                            }}
                                        >
                                            <HighlightOffIcon color="error" fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Divider sx={{ mb: 1, mt: 2, mx: '16px' }} />
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('ORDER_BY')}</InputLabel>
                                    <Select label={t('ORDER_BY')} name="orderFieldName" onChange={handleChangeOrder} value={orderBy}>
                                        <MenuItem value="name">{t('NAME')}</MenuItem>
                                        <MenuItem value="surname">{t('SURNAME')}</MenuItem>
                                        <MenuItem value="username">{t('Email')}</MenuItem>
                                        <MenuItem value="company">{t('COMPANY')}</MenuItem>
                                        <MenuItem value="mailCompany">{t('COMPANY_EMAIL')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('ORDER_METHOD')}</InputLabel>
                                    <Select
                                        label={t('ORDER_METHOD')}
                                        name="isDescending"
                                        onChange={handleChangeOrderMethod}
                                        sx={{ color: 'white' }}
                                        value={order}
                                    >
                                        <MenuItem value="asc">{t('ASCENDING')}</MenuItem>
                                        <MenuItem value="desc">{t('DESCENDING')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    startIcon={<CloseIcon fontSize="large" />}
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        setDefaultFilters();
                                        setFliterDialogModalOpen(false);
                                    }}
                                >
                                    {t('REMOVE_FILTERS')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Drawer>

                    <Grid item lg={12} xs={12} container>
                        <Grid lg={4} md={4} item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                onChange={handleSearch}
                                placeholder={mobileDevice ? t('SEARCH') : t('SEARCH_USER')}
                                value={searchText}
                            />
                        </Grid>
                        <Grid lg={4} md={4} item xs={2} zeroMinWidth />
                        <Grid
                            lg={4}
                            md={4}
                            xs={4}
                            item
                            sx={{ display: 'flex', justifyContent: mobileDevice ? 'space-around' : 'flex-end' }}
                        >
                            <Grid
                                item
                                lg={2}
                                md={2}
                                xs={windowWidth < 400 ? 3 : 4}
                                sx={{
                                    display: 'flex',
                                    justifyContent: windowWidth < 400 ? 'flex-start' : 'flex-end',
                                    textAlign: 'right'
                                }}
                            >
                                <Stack direction="row" spacing={-1}>
                                    <Tooltip title={t('FILTERS')}>
                                        <IconButton onClick={() => setFliterDialogModalOpen(true)}>
                                            <FilterAltIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'right', paddingTop: 0.9 }}>
                                <Tooltip title={t('ADD_USER')}>
                                    <IconButton
                                        className="addButton"
                                        sx={{ backgroundColor: theme.palette.primary.main }}
                                        size="small"
                                        onClick={() =>
                                            setUserToAdd({
                                                id: '',
                                                name: '',
                                                surname: '',
                                                username: '',
                                                isSystemAdmin: false,
                                                company: '',
                                                emailCompany: '',
                                                preferredLanguage: 'en',
                                                role: '',
                                                tenantId: loggedUser!.tenantId,
                                                otpEnabled: false
                                            })
                                        }
                                    >
                                        <AddIcon sx={{ color: '#fff', fontSize: '17px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
            {/* table */}
            {rows ? (
                <>
                    {mobileDevice ? (
                        <UserListMobile userList={rows} onEdit={(row) => setUserToEdit(row)} isLoading={isLoading} />
                    ) : (
                        <TableContainer sx={{ pl: '2px', pr: '2px' }}>
                            <Table
                                stickyHeader
                                sx={{
                                    minWidth: 750,
                                    '& .MuiTableCell-root': { padding: '2px 8px' }
                                }}
                                aria-labelledby="tableTitle"
                            >
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={rows.length}
                                    selected={selected}
                                />
                                {!isLoading ? (
                                    <TableBody>
                                        {rows.map((row, index) => {
                                            if (typeof row === 'number') return null;
                                            const isItemSelected = isSelected(row.name);
                                            const labelId = `enhanced-table-checkbox-${index}`;

                                            return (
                                                <TableRow
                                                    hover
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={row.id}
                                                    selected={isItemSelected}
                                                >
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {row.name ? row.name : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {row.surname ? row.surname : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {row.username}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {row.company}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {row.role ? row.role : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ pr: 3, '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                                                        <Tooltip title={t('EDIT')}>
                                                            <IconButton color="secondary" size="large" onClick={() => setUserToEdit(row)}>
                                                                <EditTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                ) : (
                                    <SkeletonBody numHeaders={5} showAction />
                                )}
                            </Table>
                        </TableContainer>
                    )}
                </>
            ) : (
                <Skeleton variant="rectangular" width={210} height={118} />
            )}
            {/* table pagination */}
            <TablePagination
                sx={{
                    ...(mobileDevice && {
                        display: 'flex',
                        '& .MuiTablePagination-actions': {
                            marginLeft: 0
                        },
                        '& .MuiInputBase-root': {
                            marginRight: 1
                        },
                        '& .MuiIconButton-root': {
                            padding: '6px'
                        }
                    })
                }}
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('OF')} ${count}`}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('ROWS_PER_PAGE')}
            />
            <UserEdit
                selectedUser={userToEdit}
                onSave={onSave}
                onDelete={onDelete}
                onClose={() => setUserToEdit(undefined)}
                systemAdmin={false}
            />
            <UserAdd user={userToAdd} onAdd={onAdd} onClose={() => setUserToAdd(undefined)} />
        </MainCard>
    );
};

export default UserList;
